import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { rolesAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Shield, Loader2, Check, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Navigate } from 'react-router-dom';

const MODULES = [
  { key: 'dashboard', name: 'Dashboard' },
  { key: 'clients', name: 'Clients' },
  { key: 'contacts', name: 'Contacts' },
  { key: 'leads', name: 'Leads' },
  { key: 'deals', name: 'Deals' },
  { key: 'quotations', name: 'Quotations' },
  { key: 'sales_orders', name: 'Sales Orders' },
  { key: 'invoices', name: 'Invoices' },
  { key: 'expenses', name: 'Expenses' },
  { key: 'suppliers', name: 'Suppliers' },
  { key: 'products', name: 'Products' },
  { key: 'purchase_orders', name: 'Purchase Orders' },
  { key: 'warehouses', name: 'Warehouses' },
  { key: 'stock', name: 'Stock' },
  { key: 'employees', name: 'Employees' },
  { key: 'departments', name: 'Departments' },
  { key: 'attendance', name: 'Attendance' },
  { key: 'payroll', name: 'Payroll' },
  { key: 'projects', name: 'Projects' },
  { key: 'tasks', name: 'Tasks' },
  { key: 'pos', name: 'POS' },
  { key: 'settings', name: 'Settings' },
];

const ACTIONS = ['create', 'read', 'update', 'delete'];

const Roles = () => {
  const { isSuperAdmin } = useAuth();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const response = await rolesAPI.getAll();
      setRoles(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  if (!isSuperAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  if (loading) {
    return <div className="flex items-center justify-center h-96"><Loader2 className="h-8 w-8 animate-spin text-teal-600" /></div>;
  }

  const hasPermission = (role, module, action) => {
    const perms = role.permissions?.[module] || [];
    return perms.includes(action);
  };

  const getPermissionCount = (role, module) => {
    const perms = role.permissions?.[module] || [];
    return perms.length;
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">Roles & Permissions</h1>
        <p className="text-slate-500 mt-1">View predefined role templates and their permissions</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {roles.map(role => (
          <Card key={role.id} className="bg-white border-slate-200">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    role.name === 'Super Admin' ? 'bg-purple-100' :
                    role.name === 'Company Admin' ? 'bg-teal-100' :
                    role.name === 'Viewer' ? 'bg-slate-100' : 'bg-blue-100'
                  }`}>
                    <Shield className={`h-5 w-5 ${
                      role.name === 'Super Admin' ? 'text-purple-700' :
                      role.name === 'Company Admin' ? 'text-teal-700' :
                      role.name === 'Viewer' ? 'text-slate-600' : 'text-blue-700'
                    }`} />
                  </div>
                  <div>
                    <CardTitle className="text-base">{role.name}</CardTitle>
                    <CardDescription className="text-xs">{role.description}</CardDescription>
                  </div>
                </div>
                {role.is_system && <Badge variant="outline">System</Badge>}
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="max-h-64 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-white">
                    <tr className="border-b">
                      <th className="text-left py-2 font-medium text-slate-500">Module</th>
                      {ACTIONS.map(action => (
                        <th key={action} className="text-center py-2 font-medium text-slate-500 capitalize w-12">{action[0].toUpperCase()}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {MODULES.map(module => {
                      const count = getPermissionCount(role, module.key);
                      if (count === 0 && role.name !== 'Super Admin' && role.name !== 'Company Admin') return null;
                      return (
                        <tr key={module.key} className="border-b border-slate-100">
                          <td className="py-1.5 text-slate-700">{module.name}</td>
                          {ACTIONS.map(action => (
                            <td key={action} className="text-center py-1.5">
                              {hasPermission(role, module.key, action) ? (
                                <Check className="h-3.5 w-3.5 text-emerald-600 mx-auto" />
                              ) : (
                                <X className="h-3.5 w-3.5 text-slate-300 mx-auto" />
                              )}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="bg-slate-50 border-slate-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Shield className="h-5 w-5 text-slate-500 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-slate-700">Permission Legend</p>
              <p className="text-xs text-slate-500 mt-1">
                <strong>C</strong> = Create, <strong>R</strong> = Read, <strong>U</strong> = Update, <strong>D</strong> = Delete
              </p>
              <p className="text-xs text-slate-500 mt-1">
                System roles are predefined and cannot be modified. Custom roles can be created for specific company needs.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Roles;
